import React, { Component } from "react";
import { DynamicFormFieldProps } from "../DynamicForm";
import { DynamicFormField, DynamicFormValidation } from "../../laco-common/base/form-validation/dynamic";
import { ISelectFormField, IFormField, COMMON_FIELD_TYPES_ARR, FormFieldType } from "../../laco-common";
import { WithOptional } from "../../laco-common/helpers/extendedTypes";
import ItemFormInput from "./ItemForm";
import { capitalize } from "../../utils";
import { generateDummyId } from "../../models/cms";
import "./FormField.scss";

const hasMultiSelect = (fieldType: FormFieldType): boolean => {
	switch (fieldType) {
		case "select":
		case "autocomplete":
		case "chips":
		case "collection":
		case "focusRow":
		case "checkbox":
			return true;
		default:
			return false;
	}
};

const getFields = (
	data: ISelectFormField
): Record<
	keyof Partial<ISelectFormField>,
	DynamicFormField & WithOptional<Partial<ISelectFormField>, "id" | "fieldType">
> => {
	return {
		label: { label: "Label", order: 10, value: data?.label, placeholder: "Identifies the form field" },
		placeholder: {
			label: "Placeholder",
			optional: true,
			order: 20,
			value: data?.placeholder,
			placeholder: "Guidance text displayed in empty text fields",
			renderWhen: (field: DynamicFormField, form: DynamicFormValidation): boolean => {
				const fieldType: FormFieldType = form.fields?.fieldType?.value as FormFieldType;
				switch (fieldType) {
					case "select":
					case "focusRow":
					case "media":
					case "phone":
					case "boolean":
					case "yes/no":
					case "checkbox":
						return false;
					default:
						return true;
				}
			},
		},
		fieldType: {
			label: "Field Type",
			order: 30,
			value: data?.fieldType,
			fieldType: "select",
			selectValues: COMMON_FIELD_TYPES_ARR.map((t: FormFieldType) => {
				return { label: capitalize(t), value: t };
			}),
		},
		order: { label: "Order", fieldType: "number", isInteger: true, value: data?.order, order: 39 },
		selectValues: {
			label: "Enter possible options or enter collection name below",
			optional: true,
			order: 35,
			value: data?.selectValues,
			fieldType: "chips",
			freeSolo: true,
			renderWhen: (field: DynamicFormField, form: DynamicFormValidation): boolean => {
				return hasMultiSelect(form.fields?.fieldType?.value as FormFieldType);
			},
		},
		collectionName: {
			label: "Firestore Collection Name",
			optional: true,
			order: 36,
			placeholder: "e.g. directors",
			renderWhen: (field: DynamicFormField, form: DynamicFormValidation): boolean => {
				return hasMultiSelect(form.fields?.fieldType?.value as FormFieldType);
			},
		},
		collectionDisplayField: {
			label: "Firestore Collection Display Field",
			optional: true,
			order: 37,
			placeholder: "e.g. name",
			renderWhen: (field: DynamicFormField, form: DynamicFormValidation): boolean => {
				return hasMultiSelect(form.fields?.fieldType?.value as FormFieldType);
			},
		},
		freeSolo: {
			label: "Allow Free Text",
			fieldType: "boolean",
			optional: true,
			order: 38,
			renderWhen: (field: DynamicFormField, form: DynamicFormValidation): boolean => {
				return hasMultiSelect(form.fields?.fieldType?.value as FormFieldType);
			},
		},
		optional: {
			label: "Field is Optional",
			optional: true,
			order: 40,
			value: data?.optional,
			fieldType: "boolean",
		},
		defaultValue: {
			label: "Default Value",
			optional: true,
			order: 50,
			value: data?.defaultValue,
			placeholder: "Prepopulated default value",
		},
		disabled: { label: "Disable Field", optional: true, order: 55, value: data?.disabled, fieldType: "boolean" },
		minLength: {
			label: "Minimum Length",
			order: 80,
			fieldType: "number",
			isInteger: true,
			value: data?.minLength,
			optional: true,
		},
		maxLength: {
			label: "Maximum Length",
			order: 85,
			fieldType: "number",
			isInteger: true,
			value: data?.maxLength,
			optional: true,
		},
		// section: { label: "Section", order: 100, value: data?.section, optional: true }, // dana collection of sections
		fromUserData: {
			label: "Populate value from user data (e.g. employee id)",
			optional: true,
			order: 160,
			value: data?.fromUserData,
			fieldType: "boolean",
		},
		userDataField: {
			label: "User data field",
			optional: true,
			order: 161,
			value: data?.userDataField,
		},
		persistUserData: {
			label: "Save value to user data",
			optional: true,
			order: 165,
			value: data?.persistUserData,
			fieldType: "boolean",
			permission: "super-admin",
		},
		// permission: { // dana permission for cms fields
		// 	label: "Permission Required",
		// 	optional: true,
		// 	order: 175,
		// 	fieldType: "collection",
		// 	collectionName: "permissions",
		// 	collectionDisplayField: "display",
		// 	useFirewire: true,
		// },
		// fields - dana TODO
	} as any; // simba
	// as Record<keyof ISelectFormField, DynamicFormField & WithOptional<Partial<ISelectFormField>, "id" | "fieldType">>;
};

export default class FormFieldInput extends Component<DynamicFormFieldProps> {
	componentDidMount() {
		console.log("FormFieldInput.componentDidMount(): props", this.props);
	}

	render() {
		let selector: string = "[id^='laco-form-input-']";
		let section: string = undefined;
		const fields: IFormField[] = this.props.field.value;
		if (fields.length && fields[0].section) {
			selector = `#laco-form-desc-section-${fields[0].section}` + selector;
			section = fields[0].section;
		}

		return (
			<ItemFormInput
				{...this.props}
				itemName="Form Field"
				nameField="label"
				viewItemSelector={selector}
				itemClassName="formfield-item"
				getFields={getFields}
				createNew={(i): IFormField => {
					return {
						label: "Sample Label",
						order: (i + 1) * 10 + 10,
						placeholder: "Help text here",
						fieldType: "text",
						id: generateDummyId(),
						section,
					};
				}}
			/>
		);
	}
}
